import { observer } from 'mobx-react-lite';
import { addAnotherEmailReminderButtonLabel } from '../strings.locstring.json';
import {
    editEmailReminderDropdownOption,
    reminderSummaryText,
} from './ReminderDropdown.locstring.json';
import { remindMe } from 'owa-locstrings/lib/strings/remindme.locstring.json';
import loc, { format } from 'owa-localize';
import isEditingEmailReminderEnabled from '../selectors/isEditingEmailReminderEnabled';
import { isOnGroupCalendar } from 'owa-calendar-event-capabilities';
import emailReminderStore from '../store/store';
import getEmailReminderSummaryText from '../utils/getEmailReminderSummaryText';
import getFilteredInboxReminders from '../utils/getFilteredInboxReminders';
import { Icon } from '@fluentui/react/lib/Icon';
import { DefaultReminderOffsets } from 'owa-calendar-reminder-options';
import ReminderOptionType from 'owa-calendar-reminder-options/lib/helpers/ReminderOptionType';
import createReminderDropdownOptions from 'owa-calendar-reminder-options/lib/utils/createReminderDropdownOptions';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import generateDefaultDropdownStyles from 'owa-component-styling-utils/lib/utils/colorSchemesToStyles/generateDefaultDropdownStyles';
import generateDropdownCalloutStyles from 'owa-component-styling-utils/lib/utils/generateDropdownCalloutStyles';
import { ControlIcons } from 'owa-control-icons';
import {
    EmailReminderDialog,
    showEmailReminderDialogLazy,
    EmailReminderEntrySource,
} from 'owa-email-reminders';

import * as React from 'react';
import { Dropdown, IDropdownOption, DropdownMenuItemType } from '@fluentui/react/lib/Dropdown';

const styles = require('./ReminderDropdown.scss');

/**
 * Props for reminder dropdown
 */
export interface ReminderDropdownProps {
    event: CalendarEvent;

    /** Callback to execute when reminder is selected from dropdown */
    onReminderChanged: (reminderInMinutes: number) => void;

    /**
     * Selected reminder time
     */
    selectedReminderTimeInMinutes: number;

    /**
     * Entry source of the email reminder component
     */
    entrySource: EmailReminderEntrySource;

    /** dropdown color scheme */
    colorScheme?: ColorScheme;

    /** Optional callback when menu is clicked to show dropdown */
    onMenuClick?: () => void;

    /** Custom css style for the control */
    className?: string;

    /** Disable the component */
    disabled?: boolean;
}

/**
 * Component for rendering the reminder dropdown
 */
export default observer(function ReminderDropdown(props: ReminderDropdownProps) {
    /**
     *  Create the options for the reminder dropdown
     *  @return options of type IDropdownOption[]
     */
    const createMenuOptions = (): IDropdownOption[] => {
        const reminderDropdownOptions: ReminderOptionType[] = createReminderDropdownOptions(
            props.event.IsAllDayEvent,
            props.selectedReminderTimeInMinutes,
            true /* includeNoReminder */
        );
        const options: IDropdownOption[] = reminderDropdownOptions.map(reminder => {
            return {
                key: reminder.key,
                text: reminder.text,
            };
        });
        if (isEditingEmailReminderEnabled(props.entrySource) && !isOnGroupCalendar(props.event)) {
            const customReminderText =
                getFilteredInboxReminders(props.event.InboxReminders).length > 0
                    ? loc(editEmailReminderDropdownOption)
                    : loc(addAnotherEmailReminderButtonLabel);
            options.push(
                {
                    key: 'divider_1',
                    text: '-',
                    itemType: DropdownMenuItemType.Divider,
                },
                {
                    key: DefaultReminderOffsets.CUSTOM_REMINDER_KEY,
                    text: customReminderText,
                }
            );
        }
        return options;
    };
    const onRenderTitle = (options: IDropdownOption[]): JSX.Element => {
        // Display the summary text in the dropdown if event has more than one inbox reminders set.
        // Otherwise fallback to the default dropdown title.
        const hasInboxRemindersToShow =
            getFilteredInboxReminders(props.event.InboxReminders).length > 0;
        let remindersSummary;
        if (hasInboxRemindersToShow) {
            remindersSummary = format(
                loc(reminderSummaryText),
                options[0].text,
                getEmailReminderSummaryText(props.event.InboxReminders)
            );
        } else {
            remindersSummary = options[0].text;
        }
        return <div>{remindersSummary}</div>;
    };
    const onRenderOption = (option: IDropdownOption): JSX.Element => {
        const isSelected = option.key === props.selectedReminderTimeInMinutes;
        return (
            <>
                <div className={styles.iconContainer}>
                    {isSelected && <Icon iconName={ControlIcons.CheckMark} />}
                </div>
                <div>{option.text}</div>
            </>
        );
    };
    /**
     * Callback issued when the menu item is invoked
     */
    const onChange = (
        ev: React.FormEvent<HTMLDivElement>,
        option?: IDropdownOption,
        index?: number
    ) => {
        if (+option.key === DefaultReminderOffsets.CUSTOM_REMINDER_KEY) {
            // If user clicks on 'Custom', show the edit email reminders dialog
            showEmailReminderDialogLazy
                .import()
                .then(showEmailReminderDialog => showEmailReminderDialog(props.entrySource));
        } else {
            // Otherwise select the email reminder in the dropdown
            props.onReminderChanged(+option.key);
        }
    };
    return (
        <>
            <Dropdown
                calloutProps={{
                    styles: generateDropdownCalloutStyles(),
                    gapSpace: 5,
                    onLayerMounted: props.onMenuClick,
                }}
                className={props.className}
                label={loc(remindMe)}
                onChange={onChange}
                onRenderTitle={onRenderTitle}
                onRenderOption={onRenderOption}
                options={createMenuOptions()}
                selectedKey={props.selectedReminderTimeInMinutes}
                styles={generateDefaultDropdownStyles(props.colorScheme)}
                disabled={props.disabled}
            />
            {emailReminderStore.showDialog && (
                <EmailReminderDialog
                    event={props.event}
                    colorScheme={props.colorScheme}
                    entrySource={props.entrySource}
                    inboxRemindersToRender={getFilteredInboxReminders(props.event.InboxReminders)}
                />
            )}
        </>
    );
});
